import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface Meeting {
  id: string;
  title: string;
  description: string;
  scheduledAt: string;
  duration: number;
  location: string;
  meetingType: string;
  meetingUrl?: string;
  status: string;
}

interface AttendanceData {
  userId: string;
  userName?: string;
  isPresent: boolean;
  attendanceType?: string;
}

interface MeetingMinutes {
  content: string;
  status: string;
  takenBy: string;
  takenByName?: string;
}

interface MeetingDocument {
  id: string;
  name: string;
  documentType: string;
  size: number;
  url: string;
}

const MeetingSummary: React.FC = () => {
  const { chamaId, meetingId } = useParams<{ chamaId: string; meetingId: string }>();
  const navigate = useNavigate();
  const colors = getThemeColors('dark');

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [meetingDetails, setMeetingDetails] = useState<Meeting | null>(null);
  const [attendanceData, setAttendanceData] = useState<AttendanceData[]>([]);
  const [meetingMinutes, setMeetingMinutes] = useState<MeetingMinutes | null>(null);
  const [meetingDocuments, setMeetingDocuments] = useState<MeetingDocument[]>([]);
  const [chamaMembers, setChamaMembers] = useState<any[]>([]);

  useEffect(() => {
    if (meetingId && chamaId) {
      loadMeetingSummaryData();
      loadChamaMembers();
    }
  }, [meetingId, chamaId]);

  const loadChamaMembers = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const response = await fetch(API_ENDPOINTS.CHAMA_MEMBERS(chamaId!), {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setChamaMembers(data.data);
        }
      }
    } catch (error) {
      console.error('❌ Failed to load chama members:', error);
    }
  };

  const loadMeetingSummaryData = async () => {
    try {
      setLoading(true);
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const [detailsResponse, attendanceResponse, minutesResponse, documentsResponse] = await Promise.allSettled([
        fetch(`${API_ENDPOINTS.MEETINGS}${meetingId}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        }),
        fetch(`${API_ENDPOINTS.MEETINGS}${meetingId}/attendance`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        }),
        fetch(`${API_ENDPOINTS.MEETINGS}${meetingId}/minutes`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        }),
        fetch(`${API_ENDPOINTS.MEETINGS}${meetingId}/documents`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        }),
      ]);

      if (detailsResponse.status === 'fulfilled' && detailsResponse.value.ok) {
        const data = await detailsResponse.value.json();
        if (data.success) {
          setMeetingDetails(data.data);
        }
      }

      if (attendanceResponse.status === 'fulfilled' && attendanceResponse.value.ok) {
        const data = await attendanceResponse.value.json();
        if (data.success) {
          setAttendanceData(data.data || []);
        }
      }

      if (minutesResponse.status === 'fulfilled' && minutesResponse.value.ok) {
        const data = await minutesResponse.value.json();
        if (data.success) {
          setMeetingMinutes(data.data);
        }
      }

      if (documentsResponse.status === 'fulfilled' && documentsResponse.value.ok) {
        const data = await documentsResponse.value.json();
        if (data.success) {
          setMeetingDocuments(data.data || []);
        }
      }
    } catch (error) {
      console.error('❌ Failed to load meeting summary data:', error);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadMeetingSummaryData();
    setRefreshing(false);
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatTime = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Time';
    }
  };

  const getMeetingTypeIcon = (type: string) => {
    switch (type) {
      case 'virtual': return '📹';
      case 'physical': return '📍';
      case 'hybrid': return '🌐';
      default: return '📅';
    }
  };

  const getAttendanceStats = () => {
    let totalMembers = chamaMembers.length || 0;
    if (totalMembers === 0 && attendanceData && attendanceData.length > 0) {
      totalMembers = attendanceData.length;
    }
    if (!attendanceData || !Array.isArray(attendanceData) || attendanceData.length === 0) {
      return { present: 0, absent: totalMembers, total: totalMembers };
    }
    const present = attendanceData.filter(att => att && att.isPresent).length;
    const absent = Math.max(0, totalMembers - present);
    return { present, absent, total: totalMembers };
  };

  // Helper function to get recorder name
  const getRecorderName = (minutes: MeetingMinutes) => {
    if (!minutes || !minutes.takenBy) {
      return 'Unknown';
    }

    if (minutes.takenByName && minutes.takenByName.length < 30 && !minutes.takenByName.includes('-')) {
      return minutes.takenByName;
    }

    const member = chamaMembers.find(m =>
      m.user_id === minutes.takenBy ||
      m.id === minutes.takenBy ||
      m.userId === minutes.takenBy
    );

    if (member) {
      const name = member.name ||
                    member.user_name ||
                    member.username ||
                    member.displayName ||
                    member.user?.name ||
                    member.user?.username ||
                    member.user?.user_name ||
                    `${member.first_name || member.user?.first_name || ''} ${member.last_name || member.user?.last_name || ''}`.trim() ||
                    member.user?.email?.split('@')[0];

      if (name && name !== ' ') {
        return name;
      }
    }

    return `Member ${minutes.takenBy.slice(-4)}`;
  };

  // Helper function to get attendee name
  const getAttendeeName = (attendance: AttendanceData) => {
    const providedName = attendance.userName;
    if (providedName && providedName.length < 30 && !providedName.includes('-')) {
      return providedName;
    }

    const member = chamaMembers.find(m =>
      m.user_id === attendance.userId ||
      m.id === attendance.userId ||
      m.userId === attendance.userId
    );

    if (member) {
      const name = member.name ||
                    member.user_name ||
                    member.username ||
                    member.displayName ||
                    member.user?.name ||
                    member.user?.username ||
                    member.user?.user_name ||
                    `${member.first_name || member.user?.first_name || ''} ${member.last_name || member.user?.last_name || ''}`.trim() ||
                    member.user?.email?.split('@')[0];

      if (name && name !== ' ') {
        return name;
      }
    }

    return `Member ${attendance.userId?.slice(-4) || 'Unknown'}`;
  };

  const handleDocumentPress = async (document: MeetingDocument) => {
    if (!document.url) {
      alert('Document URL Missing');
      return;
    }

    try {
      // For web, open in new tab/window
      window.open(document.url, '_blank');
    } catch (error) {
      console.error('❌ Document handling error:', error);
      alert('Unable to process document request');
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>Loading meeting summary...</div>
        </div>
      </div>
    );
  }

  const stats = getAttendanceStats();
  const attendanceRate = stats.total > 0 ? Math.round((stats.present / stats.total) * 100) : 0;

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Meeting Summary"
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Meeting Header */}
          {meetingDetails && (
            <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.meetingHeader}>
                <div style={{
                  ...styles.meetingTypeIcon,
                  backgroundColor: colors.primary + '20'
                }}>
                  <span style={{ fontSize: '24px' }}>
                    {getMeetingTypeIcon(meetingDetails.meetingType)}
                  </span>
                </div>
                <div style={styles.meetingHeaderInfo}>
                  <h2 style={{ ...styles.meetingTitle, color: colors.text }}>
                    {meetingDetails.title}
                  </h2>
                  <p style={{ ...styles.meetingDate, color: colors.textSecondary }}>
                    {formatDate(meetingDetails.scheduledAt)}
                  </p>
                  <p style={{ ...styles.meetingTime, color: colors.textSecondary }}>
                    {formatTime(meetingDetails.scheduledAt)} • {meetingDetails.meetingType?.charAt(0).toUpperCase() + meetingDetails.meetingType?.slice(1)}
                  </p>
                  {meetingDetails.location && (
                    <p style={{ ...styles.meetingLocation, color: colors.textSecondary }}>
                      📍 {meetingDetails.location}
                    </p>
                  )}
                </div>
              </div>
              {meetingDetails.description && (
                <p style={{
                  ...styles.meetingDescription,
                  color: colors.textSecondary,
                  borderTop: `1px solid ${colors.border}`,
                  paddingTop: spacing.md
                }}>
                  {meetingDetails.description}
                </p>
              )}
            </div>
          )}

          {/* Attendance Summary */}
          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Attendance Summary</h3>
            <div style={styles.attendanceStats}>
              <div style={styles.statItem}>
                <div style={{ ...styles.statValue, color: colors.success }}>{stats.present}</div>
                <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Present</div>
              </div>
              <div style={styles.statItem}>
                <div style={{ ...styles.statValue, color: colors.error }}>{stats.absent}</div>
                <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Absent</div>
              </div>
              <div style={styles.statItem}>
                <div style={{ ...styles.statValue, color: colors.primary }}>{attendanceRate}%</div>
                <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Rate</div>
              </div>
            </div>
          </div>

          {/* Attendance Details */}
          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Attendance Details</h3>
            {attendanceData.length > 0 ? (
              <>
                {/* Present Members */}
                <div style={styles.attendanceSection}>
                  <h4 style={{ ...styles.attendanceSectionTitle, color: colors.success }}>Present ({attendanceData.filter(att => att.isPresent).length})</h4>
                  <div style={styles.attendanceList}>
                    {attendanceData.filter(att => att.isPresent).map((attendance, index) => (
                      <div key={`present-${index}`} style={styles.attendanceItem}>
                        <div style={styles.attendanceInfo}>
                          <div style={{ ...styles.attendeeName, color: colors.text }}>
                            {getAttendeeName(attendance)}
                          </div>
                          <div style={{ ...styles.attendanceType, color: colors.textSecondary }}>
                            {attendance.attendanceType || 'physical'} attendance
                          </div>
                        </div>
                        <div style={{
                          ...styles.attendanceStatus,
                          backgroundColor: colors.success + '20'
                        }}>
                          <span style={{
                            fontSize: '16px',
                            color: colors.success
                          }}>
                            ✓
                          </span>
                          <span style={{
                            ...styles.attendanceStatusText,
                            color: colors.success
                          }}>
                            Present
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Absent Members */}
                {attendanceData.filter(att => !att.isPresent).length > 0 && (
                  <div style={styles.attendanceSection}>
                    <h4 style={{ ...styles.attendanceSectionTitle, color: colors.error }}>Absent ({attendanceData.filter(att => !att.isPresent).length})</h4>
                    <div style={styles.attendanceList}>
                      {attendanceData.filter(att => !att.isPresent).map((attendance, index) => (
                        <div key={`absent-${index}`} style={styles.attendanceItem}>
                          <div style={styles.attendanceInfo}>
                            <div style={{ ...styles.attendeeName, color: colors.text }}>
                              {getAttendeeName(attendance)}
                            </div>
                            <div style={{ ...styles.attendanceType, color: colors.textSecondary }}>
                              {attendance.attendanceType || 'physical'} attendance
                            </div>
                          </div>
                          <div style={{
                            ...styles.attendanceStatus,
                            backgroundColor: colors.error + '20'
                          }}>
                            <span style={{
                              fontSize: '16px',
                              color: colors.error
                            }}>
                              ✗
                            </span>
                            <span style={{
                              ...styles.attendanceStatusText,
                              color: colors.error
                            }}>
                              Absent
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={styles.emptyState}>
                <span style={{ fontSize: '48px' }}>👥</span>
                <div style={{ ...styles.emptyStateText, color: colors.textSecondary }}>
                  No attendance data available
                </div>
              </div>
            )}
          </div>

          {/* Meeting Minutes */}
          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Meeting Minutes</h3>
            {meetingMinutes ? (
              <>
                <div style={{
                  ...styles.minutesContent,
                  color: colors.textSecondary,
                  backgroundColor: colors.background,
                  border: `1px solid ${colors.border}`
                }}>
                  {meetingMinutes.content}
                </div>
                <div style={styles.minutesMetadata}>
                  <div style={{ ...styles.minutesStatus, color: colors.primary }}>
                    Status: {meetingMinutes.status?.charAt(0).toUpperCase() + meetingMinutes.status?.slice(1)}
                  </div>
                  <div style={{ ...styles.minutesTakenBy, color: colors.textSecondary }}>
                    Recorded by: {getRecorderName(meetingMinutes)}
                  </div>
                </div>
              </>
            ) : (
              <div style={styles.emptyState}>
                <span style={{ fontSize: '48px' }}>📝</span>
                <div style={{ ...styles.emptyStateText, color: colors.textSecondary }}>
                  No meeting minutes available
                </div>
              </div>
            )}
          </div>

          {/* Meeting Documents */}
          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <h3 style={{ ...styles.sectionTitle, color: colors.text }}>
              Meeting Documents ({meetingDocuments.length})
            </h3>
            {meetingDocuments.length > 0 ? (
              <>
                {meetingDocuments.map((document, index) => (
                  <div
                    key={index}
                    style={{
                      ...styles.documentItem,
                      borderBottom: index < meetingDocuments.length - 1 ? `1px solid ${colors.border}` : 'none',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleDocumentPress(document)}
                  >
                    <div style={{
                      ...styles.documentIcon,
                      backgroundColor: colors.primary + '20'
                    }}>
                      <span style={{ fontSize: '20px', color: colors.primary }}>📄</span>
                    </div>
                    <div style={styles.documentInfo}>
                      <div style={{ ...styles.documentName, color: colors.text }}>
                        {document.name}
                      </div>
                      <div style={{ ...styles.documentType, color: colors.textSecondary }}>
                        {document.documentType} • {Math.round(document.size / 1024)} KB
                      </div>
                    </div>
                    <span style={{ fontSize: '20px', color: colors.primary }}>📥</span>
                  </div>
                ))}
              </>
            ) : (
              <div style={styles.emptyState}>
                <span style={{ fontSize: '48px' }}>📁</span>
                <div style={{ ...styles.emptyStateText, color: colors.textSecondary }}>
                  No documents available
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={styles.actionButtons}>
            {meetingDetails && (meetingDetails.meetingType === 'physical' || meetingDetails.meetingType === 'hybrid') && (
              <button
                onClick={() => navigate(`/dashboard/chama/${chamaId}/meetings/${meetingId}/attendance/physical`)}
                style={{ ...styles.actionButton, backgroundColor: colors.info }}
              >
                Mark Physical Attendance
              </button>
            )}
            {meetingDetails && (meetingDetails.meetingType === 'virtual' || meetingDetails.meetingType === 'hybrid') && (
              <button
                onClick={() => navigate(`/dashboard/chama/${chamaId}/meetings/${meetingId}/attendance/online`)}
                style={{ ...styles.actionButton, backgroundColor: colors.secondary }}
              >
                Mark Online Attendance
              </button>
            )}
            <button
              onClick={onRefresh}
              disabled={refreshing}
              style={{
                ...styles.actionButton,
                backgroundColor: refreshing ? colors.textSecondary : colors.primary,
                cursor: refreshing ? 'not-allowed' : 'pointer',
              }}
            >
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    paddingBottom: 80,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: 80,
    paddingBottom: 80,
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: spacing.md,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxxl,
  },
  card: {
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    marginBottom: spacing.md,
    ...getShadowStyle('sm'),
  },
  meetingHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: spacing.md,
  },
  meetingTypeIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  meetingHeaderInfo: {
    flex: 1,
  },
  meetingTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    margin: 0,
    marginBottom: spacing.xs,
  },
  meetingDate: {
    fontSize: typography.fontSize.base,
    margin: 0,
    marginBottom: spacing.xs / 2,
  },
  meetingTime: {
    fontSize: typography.fontSize.sm,
    margin: 0,
    marginBottom: spacing.xs / 2,
  },
  meetingLocation: {
    fontSize: typography.fontSize.sm,
    margin: 0,
  },
  meetingDescription: {
    fontSize: typography.fontSize.base,
    lineHeight: 1.5,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    margin: 0,
    marginBottom: spacing.md,
  },
  attendanceStats: {
    display: 'flex',
    justifyContent: 'space-around',
    marginBottom: spacing.md,
    paddingTop: spacing.md,
    paddingBottom: spacing.md,
    borderRadius: borderRadius.md,
    backgroundColor: 'rgba(0, 212, 170, 0.05)',
  },
  statItem: {
    textAlign: 'center',
  },
  statValue: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.bold,
    marginBottom: spacing.xs / 2,
  },
  statLabel: {
    fontSize: typography.fontSize.sm,
  },
  attendanceSection: {
    marginBottom: spacing.lg,
  },
  attendanceSectionTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    margin: 0,
    marginBottom: spacing.sm,
  },
  attendanceList: {
    marginTop: spacing.sm,
  },
  attendanceItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  },
  attendanceInfo: {
    flex: 1,
  },
  attendeeName: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.xs / 2,
  },
  attendanceType: {
    fontSize: typography.fontSize.sm,
  },
  attendanceStatus: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: spacing.sm,
    paddingRight: spacing.sm,
    paddingTop: spacing.xs,
    paddingBottom: spacing.xs,
    borderRadius: borderRadius.sm,
    gap: spacing.xs,
  },
  attendanceStatusText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  minutesContent: {
    fontSize: typography.fontSize.base,
    lineHeight: 1.6,
    marginBottom: spacing.md,
    padding: spacing.md,
    borderRadius: borderRadius.md,
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  },
  minutesMetadata: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  minutesStatus: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  minutesTakenBy: {
    fontSize: typography.fontSize.sm,
  },
  documentItem: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
  },
  documentIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  documentInfo: {
    flex: 1,
  },
  documentName: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.xs / 2,
  },
  documentType: {
    fontSize: typography.fontSize.sm,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: spacing.xl,
    paddingBottom: spacing.xl,
    paddingLeft: spacing.lg,
    paddingRight: spacing.lg,
  },
  emptyStateText: {
    fontSize: typography.fontSize.base,
    textAlign: 'center',
    marginTop: spacing.md,
    lineHeight: 1.5,
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
  actionButton: {
    width: '100%',
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
};

export default MeetingSummary;